const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
const FLV_MOTION_REVEAL_CLASSNAME = 'flv-motion-reveal';
const FLV_MOTION_WIPE_CLASSNAME = 'flv-motion-wipe';
const FLV_SECTION_WIPE_SELECTORS = [
  '.flv-home-intro',
  '.flv-home-carousel',
  '.flv-media-feature',
  '.flv-story-impact',
  '.flv-founder-portrait-story',
  '.site-footer',
];
const FLV_REVEAL_SELECTORS = [
  '.flv-video-hero__graphic',
  '.flv-video-hero__cta-wrap',
  '.flv-home-intro__inner > *',
  '.flv-home-carousel__copy > *',
  '.flv-home-carousel__cta',
  '.flv-media-feature__media',
  '.flv-media-feature__content > *',
  '.flv-story-impact__media',
  '.flv-story-impact__content > *',
  '.flv-founder-portrait-story__media',
  '.flv-founder-portrait-story__panel',
  '.site-footer__brand-block',
  '.site-footer__link-group',
  '.site-footer__newsletter',
  '.site-footer__social',
  '.site-footer__follow',
  '.site-footer__payment',
  '.site-footer__legal',
  '.site-footer__bottom > *',
];
const FLV_CASCADE_SELECTORS = ['.flv-home-carousel .grid__item'];

function getQueryRoot(rootEl = document) {
  if (rootEl instanceof Element) return rootEl;
  return document;
}

function getElementsFromSelectors(rootEl, selectors) {
  const queryRoot = getQueryRoot(rootEl);
  const elements = [];

  selectors.forEach((selector) => {
    if (queryRoot instanceof Element && queryRoot.matches(selector)) elements.push(queryRoot);
    elements.push(...queryRoot.querySelectorAll(selector));
  });

  return [...new Set(elements)];
}

function markFloridaVibesReveal(element) {
  element.classList.add(FLV_MOTION_REVEAL_CLASSNAME);

  if (!element.style.getPropertyValue('--flv-motion-drift-x') && element.classList.contains('animate--slide-in')) {
    element.style.setProperty('--flv-motion-drift-x', '0.35rem');
  }
}

function attachFloridaVibesReveal(element, animationName, index = 0) {
  element.classList.add(SCROLL_ANIMATION_TRIGGER_CLASSNAME, `animate--${animationName}`);
  markFloridaVibesReveal(element);
  element.style.setProperty('--animation-order', `${index}`);

  if (animationName === 'slide-in') {
    const driftX = index % 2 === 0 ? '-0.45rem' : '0.45rem';
    element.style.setProperty('--flv-motion-drift-x', driftX);
  }

  element.dataset.flvMotionBound = 'true';
}

function decorateFloridaVibesMotion(rootEl = document) {
  const queryRoot = getQueryRoot(rootEl);
  const existingScrollTriggers = [
    ...(queryRoot instanceof Element && queryRoot.classList.contains(SCROLL_ANIMATION_TRIGGER_CLASSNAME) ? [queryRoot] : []),
    ...queryRoot.querySelectorAll(`.${SCROLL_ANIMATION_TRIGGER_CLASSNAME}`),
  ];

  existingScrollTriggers.forEach((element, index) => {
    markFloridaVibesReveal(element);

    if (element.hasAttribute('data-cascade') && !element.style.getPropertyValue('--animation-order')) {
      element.style.setProperty('--animation-order', `${index}`);
    }
  });

  getElementsFromSelectors(rootEl, FLV_SECTION_WIPE_SELECTORS).forEach((element) => {
    element.classList.add(FLV_MOTION_WIPE_CLASSNAME);

    if (!element.dataset.flvMotionBound) {
      attachFloridaVibesReveal(element, 'fade-in');
    }
  });

  getElementsFromSelectors(rootEl, FLV_REVEAL_SELECTORS).forEach((element) => {
    if (!element.dataset.flvMotionBound) {
      attachFloridaVibesReveal(element, 'slide-in');
    }
  });

  getElementsFromSelectors(rootEl, FLV_CASCADE_SELECTORS).forEach((element, index) => {
    if (!element.dataset.flvMotionBound) {
      attachFloridaVibesReveal(element, 'slide-in', index);
    }
  });
}

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.style.setProperty('--animation-order', `${index}`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  decorateFloridaVibesMotion();
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    decorateFloridaVibesMotion(event.target);
    initializeScrollAnimationTrigger(event.target, true);
  });
  document.addEventListener('shopify:section:reorder', () => {
    decorateFloridaVibesMotion(document);
    initializeScrollAnimationTrigger(document, true);
  });
}
